import React, { useEffect, useCallback, useState } from "react";
import EditMenu from "./EditMenu";
import CustomerToolbar from "./customers/components/CustomerToolbar";
import CustomerTable from "./customers/components/CustomerTable";
import LoadingState from "./customers/components/LoadingState";
import ErrorState from "./customers/components/ErrorState";
import { useCustomerData } from "./customers/hooks/useCustomerData";
import { useCustomerSearch } from "./customers/hooks/useCustomerSearch";
import { usePagination } from "./customers/hooks/usePagination";
import { useCustomerDialogs } from "./customers/hooks/useCustomerDialogs";
import { CustomerData as CustomerDataType } from "./../../services/interfacev2";
import CustomerDialogs from "./customers/components/CustomerDialogs";
import { PageTitle } from "src/shared/PageTitle";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { postCustomer } from "src/shared/services/servicesv2";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { pageTitle } from "src/shared/utils/brand";
import { useUserTheme } from "src/shared/utils/useUserTheme";

const CustomerDataPage: React.FC = () => {
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, setPage, } = usePagination(10);
  const { selectedCustomer, isEditOpen, isDeleteOpen, isAddOpen, openEdit, openDelete, openAdd, closeEdit, closeDelete, closeAdd, resetSelection } = useCustomerDialogs();
  const [, setBusinessType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const { customerData, loading, error, refetch } = useCustomerData({showDeleted, searchTerm, page, rowsPerPage});
  const { filteredData } = useCustomerSearch(customerData?.customer_details, showDeleted);
  const { sidebarThemeClass } = useUserTheme();

  useEffect(() => {
    document.title = pageTitle("Customers › Settings");
    setBusinessType(sessionStorage.getItem("businessType") || "");
  }, []);
  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setPage(0);
    },
    [setPage]
  );

  const handleToggleDeleted = useCallback(() => {
    setShowDeleted((prev) => !prev);
    setSearchTerm("");
    setPage(0);
  }, [setPage]);

  const handleAddCustomer = useCallback(
    async (customerData: Partial<CustomerDataType>) => {
      try {
        const response = await postCustomer(customerData);
        if (response.data) {
          notifySuccess("Customer added successfully");
          closeAdd();
          setSearchTerm("");
          refetch();
        }
      } catch (err: any) {
        const message = err.response?.data?.message || (err.response?.status === 409 ? "Duplicate entry" : "Error adding customer");
        notifyError(message);
        console.error("Error adding customer:", err);
      }
    },
    [refetch, closeAdd]
  );

  const handleCustomerTablePageChange = (newPage: number) => {
    handleChangePage(null, newPage);
  };

  const handleCustomerTableRowsChange = (newRowsPerPage: number) => {
    handleChangeRowsPerPage({
      target: { value: newRowsPerPage.toString() },
    } as React.ChangeEvent<HTMLInputElement>);
    setPage(0);
  };

  return (
    <div className="p-1">
      <div className={`flex flex-col w-full shadow-inner rounded-lg h-[91vh] items-center ${sidebarThemeClass} overflow-hidden`}>
        <PageTitle title="- Settings › Customers" />
        <EditMenu editmenu={{ Customer: "customer" }} />

        {/* Toolbar */}
        <div className='customer-add-container'>
          <CustomerToolbar
            searchTerm={searchTerm}
            showDeleted={showDeleted}
            onSearchChange={handleSearch}
            onToggleDeleted={handleToggleDeleted}
            onAddCustomer={openAdd}
          />
        </div>

        {/* States */}
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && (
          <CustomerTable
            data={filteredData || []}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRecords={customerData?.totalRecords || 0}
            showDeleted={showDeleted}
            onEdit={openEdit}
            onDelete={(id) => {const customer = customerData?.customer_details.find(c => c._id.$oid === id);
              if (customer) openDelete(customer);
            }}
            onPageChange={handleCustomerTablePageChange}
            onRowsPerPageChange={handleCustomerTableRowsChange}
          />
        )}

        {/* Dialogs */}
        <CustomerDialogs
          isEditOpen={isEditOpen}
          isDeleteOpen={isDeleteOpen}
          isAddOpen={isAddOpen}
          selectedCustomer={selectedCustomer}
          onCloseEdit={closeEdit}
          onCloseDelete={closeDelete}
          onCloseAdd={closeAdd}
          onAddCustomer={handleAddCustomer}
          onRefetch={refetch}
          onResetSelection={resetSelection}
        />
        {loading && <LoadingScreen />}
        {/* <ToastContainer /> */}
      </div>
    </div>
  );
};

export default CustomerDataPage;